// components/Settings/knowledge/data/DataTab.jsx
import { useEffect, useState } from "react";
import {
  getSelectedTablesForKnowledge,
  getTableSchemaForKnowledge,
  saveTableKnowledge,
} from "../../../../apiWithKnowledge";
import { 
  Edit2, 
  ArrowLeft, 
  X, 
  Table, 
  FileText, 
  Database, 
  Tags, 
  Save,
  Loader2,
  Search
} from "lucide-react";
import { toast } from "react-toastify";
import { useIngestionJobs } from "../../ingestion/IngestionJobsContext";
import { IngestionQueueStrip, IngestionStatusBadge } from "../../ingestion/IngestionFeedback";

export default function DataTab({ configId }) {
  const [tables, setTables] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [tableDescription, setTableDescription] = useState("");
  const [columns, setColumns] = useState([]);

  const [editingColumn, setEditingColumn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeKnowledgeId, setActiveKnowledgeId] = useState(null);
  const { registerJob, getJob, retryJob } = useIngestionJobs({
    sourceDbId: configId,
    resourceTypes: ["table_metadata"],
  });

  /* Load tables */
  useEffect(() => {
    if (!configId) return;

    setActiveTable(null);
    setLoading(true);

    getSelectedTablesForKnowledge(configId)
      .then(({ data }) => {
        setTables(data?.selected_tables || []);
      })
      .catch(() => toast.error("Failed to load tables"))
      .finally(() => setLoading(false));
  }, [configId]);

  /* Load table schema */
  const loadTable = async (tableName) => {
    setActiveTable(tableName);
    setActiveKnowledgeId(null);
    setLoading(true);

    try {
      const { data } = await getTableSchemaForKnowledge({
        config_id: configId,
        table_name: tableName,
      });

      setTableDescription(data.table_description || "");
      setColumns(data.columns || []);
      setActiveKnowledgeId(data.id ?? null);
    } catch {
      toast.error("Failed to load table details");
    } finally {
      setLoading(false);
    }
  };

  /* Save knowledge */
  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await saveTableKnowledge({
        config_id: configId,
        table_name: activeTable,
        table_description: tableDescription,
        columns,
      });
      const knowledgeId = data?.id ?? activeKnowledgeId;
      setActiveKnowledgeId(knowledgeId);
      if (data?.ingestion) {
        registerJob({
          summary: data.ingestion,
          sourceDbId: configId,
          resourceId: knowledgeId,
          label: activeTable,
        });
      }
      toast.success("Knowledge saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const filteredTables = tables.filter((t) =>
    t.table_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-background">
      <IngestionQueueStrip
        className="mx-6 mt-4"
        sourceDbId={configId}
        resourceTypes={["table_metadata"]}
      />

      {/* ================= TABLE LIST VIEW ================= */}
      {!activeTable && (
        <div className="no-scrollbar h-full overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-4">
            
            {/* Compact Search */}
            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search tables..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>

            {/* Tables Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-primary" />
              </div>
            ) : filteredTables.length === 0 ? (
              <div className="text-center py-12">
                <Table size={28} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? "No tables found" : "No tables available"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                {filteredTables.map((t) => (
                  <button
                    key={t.table_name}
                    onClick={() => loadTable(t.table_name)}
                    className="group text-left relative overflow-hidden ring-1 ring-border border-border rounded-lg p-4 bg-card hover:bg-accent/50 transition-all duration-200 hover:ring-primary/50"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 bg-primary/10 rounded-md group-hover:bg-primary/20 transition-colors flex-shrink-0">
                        <Table size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                          {t.table_name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Configure
                        </p>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/*  TABLE DETAIL VIEW  */}
      {activeTable && (
        <div className="h-full flex flex-col">
          
          {/* Scrollable Content */}
          <div className="no-scrollbar flex-1 min-h-0 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-6 py-4">

              {/* Compact Back Button */}
              <button
                onClick={() => setActiveTable(null)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors group"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
                <span>Back</span>
              </button>

              {/* Table Description */}
              <div className=" no-scrollbar bg-card ring-1 ring-border border-border rounded-lg shadow-sm mb-4 overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold">{activeTable}</h3>
                    <IngestionStatusBadge
                      compact
                      job={activeKnowledgeId === null ? null : getJob("table_metadata", activeKnowledgeId)}
                      onRetry={() => retryJob(getJob("table_metadata", activeKnowledgeId)?.jobId)}
                    />
                  </div>
                </div>
                <div className="p-4">
                  <textarea
                    rows={3}
                    value={tableDescription}
                    onChange={(e) => setTableDescription(e.target.value)}
                    placeholder="Describe what this table represents..."
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  />
                </div>
              </div>

              {/* Columns Table */}
              <div className="no-scrollbar bg-card ring-1 ring-border  border-border rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-2.5 bg-muted/30 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database size={16} className="text-primary" />
                      <h3 className="text-sm font-semibold">Columns ({columns.length})</h3>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="no-scrollbar overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-muted/20 z-10">
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                            Column
                          </th>
                          <th className="text-left py-2 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                            Description
                          </th>
                          <th className="text-right py-2 px-4 font-semibold text-xs uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {columns.map((col) => (
                          <tr
                            key={col.column_name}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <td className="py-3 px-4 align-top">
                              <code className="px-2 py-0.5 bg-muted/50 rounded text-xs font-mono font-medium whitespace-nowrap">
                                {col.column_name}
                              </code>
                            </td>
                            <td className="py-3 px-4 align-top">
                              <div className="max-w-2xl">
                                {col.description ? (
                                  <p className="text-sm text-foreground line-clamp-2">
                                    {col.description}
                                  </p>
                                ) : (
                                  <span className="text-sm text-muted-foreground italic">
                                    No description
                                  </span>
                                )}
                                {col.synonyms && col.synonyms.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {col.synonyms.map((syn, i) => (
                                      <span
                                        key={i}
                                        className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium"
                                      >
                                        {syn}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right align-top">
                              <button
                                onClick={() => setEditingColumn(col)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-all text-xs font-medium whitespace-nowrap"
                              >
                                <Edit2 size={12} />
                                <span>Edit</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Compact Fixed Save Button */}
          <div className="flex-shrink-0 border-t bg-background shadow-lg">
            <div className="max-w-7xl mx-auto px-6 py-2.5">
              <div className="flex items-center justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      <span>Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT MODAL ================= */}
      {editingColumn && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="no-scrollbar w-full max-w-2xl bg-background rounded-xl shadow-2xl ring-1 ring-border overflow-hidden">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b bg-muted/30">
              <div className="flex items-center gap-2">
                <Edit2 size={16} className="text-primary" />
                <h2 className="font-semibold text-sm">
                  Edit: <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono ml-1">
                    {editingColumn.column_name}
                  </code>
                </h2>
              </div>
              <button
                onClick={() => setEditingColumn(null)}
                className="p-1.5 hover:bg-muted rounded-md transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="no-scrollbar p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              
              {/* Description */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-primary" />
                  <label className="text-sm font-medium">Description</label>
                </div>
                <textarea
                  rows={4}
                  value={editingColumn.description || ""}
                  onChange={(e) =>
                    setEditingColumn({
                      ...editingColumn,
                      description: e.target.value,
                    })
                  }
                  placeholder="Describe what this column represents..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                />
              </div>

              {/* Synonyms */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tags size={14} className="text-primary" />
                  <label className="text-sm font-medium">Synonyms</label>
                </div>
                <input
                  type="text"
                  value={(editingColumn.synonyms || []).join(", ")}
                  onChange={(e) =>
                    setEditingColumn({
                      ...editingColumn,
                      synonyms: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="e.g., customer name, client name"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Comma-separated alternative terms
                </p>
              </div>

              {/* Preview */}
              {(editingColumn.description || (editingColumn.synonyms || []).length > 0) && (
                <div className="pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">PREVIEW</p>
                  <div className="p-3 bg-muted/30 rounded-lg border">
                    <code className="text-xs font-mono font-semibold">
                      {editingColumn.column_name}
                    </code>
                    {editingColumn.description && (
                      <p className="text-sm mt-2">{editingColumn.description}</p>
                    )}
                    {editingColumn.synonyms && editingColumn.synonyms.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {editingColumn.synonyms.map((syn, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium"
                          >
                            {syn}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/20">
              <button
                onClick={() => setEditingColumn(null)}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-border hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setColumns((cols) =>
                    cols.map((c) =>
                      c.column_name === editingColumn.column_name
                        ? editingColumn
                        : c
                    )
                  );
                  setEditingColumn(null);
                  toast.success("Column updated");
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Save
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
